import { AutocompleteBaseProps } from 'components/forms/fields/autocomplete-field/autocomplete-base/autocomplete-base.component';
import { DateRangeBaseFieldProps } from 'components/forms/fields/date-range-field/date-range-base-field/date-range-base-field.component';
import { DateRangeOption } from 'components/forms/fields/date-range-field/date-range-base-field/date-range-base-field.utils';
import { IncTextFieldBaseProps } from 'components/forms/fields/inc-text-field/inc-text-input-base/inc-text-input-base.component';
import { SelectBaseProps } from 'components/forms/fields/select-field/select-base/select-base.component';
import { ReactNode, createContext } from 'react';
import { Option } from 'util/multi-select';
import { UseSearchFn, UseSearchQueryFn, useSearchQuery } from './search.hooks';
import { DateTime } from 'luxon';

export type FilterType =
  | MultiSelectFilter
  | SingleSelectFilter
  | DateRangeFilter
  | TextInputFilter
  | ComparisonDateRangeFilter;

export type DateRangeFilterComponentProps = {
  selectBase?: Partial<SelectBaseProps>;
  dateRangeBaseField?: Partial<DateRangeBaseFieldProps>;
};

type BaseFilterType = {
  type: string;
  label: string;
  focus?: () => void;
  clearable?: boolean;
};

type MultiSelectFilter = BaseFilterType & {
  type: 'MultiSelectFilter';
  componentProps?: Partial<AutocompleteBaseProps>;
};

type SingleSelectFilter = BaseFilterType & {
  type: 'SingleSelectFilter';
  componentProps?: Partial<AutocompleteBaseProps>;
};

type TextInputFilter = BaseFilterType & {
  type: 'TextInputFilter';
  componentProps?: Partial<IncTextFieldBaseProps>;
};

type DateRangeFilter = BaseFilterType & {
  type: 'DateRangeFilter';
  startParamName: string;
  endParamName: string;
  predefinedDateOptions?: DateRangeOption[];
  showName?: boolean;
  maxDate?: DateTime;
  anchorDate?: DateTime;
  /**
   * When true, only the preset dropdown is shown and the custom date-range
   * picker is hidden — the user can pick a preset but not an arbitrary range.
   */
  presetsOnly?: boolean;
  componentProps?: DateRangeFilterComponentProps;
};

type ComparisonDateRangeFilter = BaseFilterType & {
  type: 'ComparisonDateRangeFilter';
  startParamName: string;
  endParamName: string;
  compareStartParamName: string;
  compareEndParamName: string;
  showName?: boolean;
  componentProps?: DateRangeFilterComponentProps;
};

export type QueryType = BaseQueryType | CategoryQueryType;

type BaseQueryType = {
  query: boolean;
};

type CategoryQueryType = Partial<BaseQueryType> & {
  category: string;
};

export type FilterTimeZone = {
  paramName: string;
};

export type SearchParam<T = unknown> = {
  paramName: string;
  defaultValue: T;
  toUrlString: (value: T) => string;
  toQueryString?: (
    value: T,
    searchParams?: {
      [key: string]: SearchParam;
    },
  ) => string;
  parse: (value: string) => T;
  value: T;
  filter?: FilterType;
  options?: Option[];
  asyncOptions?: (searchParamsValues: {
    [key: string]: any;
  }) => Promise<Option[]>;
  asyncDependents?: string[];
  category?: string;
  query?: boolean;
  timeZone?: FilterTimeZone;
  excludeFromQuery?: boolean;
  excludeFromUrl?: boolean;
  allOptionId?: string;
};

export type SetParamFn = <T>(
  paramName: string,
  value: Partial<SearchParam<T>>,
) => void;
export type SetParamValueFn = <T>(paramName: string, value: T) => void;

type PaginationModelType = {
  page: number;
  pageSize: number;
};

export type SetPaginationModelFn = (
  PaginationModel: PaginationModelType,
) => void;

export type SearchContextType = {
  paging: any;
  searchResults: any;
  data: any;
  queryState: any;
  setParam: SetParamFn;
  setParamValue: SetParamValueFn;
  setPaginationModel: SetPaginationModelFn;
  removeParams: (paramNames: string[]) => void;
  resetParam: (paramName: string) => void;
  resetAllParams: () => void;
  setParamFocus: (paramName: string, focus: () => void) => void;
  searchParams: { [key: string]: SearchParam };
  searchFilters: { [key: string]: SearchParam };
  categories: { [key: string]: SearchParam };
  queryParam: SearchParam;
};

export const SearchContext = createContext<SearchContextType>(
  {} as SearchContextType,
);

export type SearchParamsContextType = {
  setParam: SetParamFn;
  setParamValue: SetParamValueFn;
  setPaginationModel: SetPaginationModelFn;
  removeParams: (paramNames: string[]) => void;
  resetParam: (paramName: string) => void;
  resetAllParams: () => void;
  setParamFocus: (paramName: string, focus: () => void) => void;
  searchParams: { [key: string]: SearchParam };
  searchFilters: { [key: string]: SearchParam };
  categories: { [key: string]: SearchParam };
  queryParam: SearchParam;
};

export const SearchParamsContext =
  createContext<SearchParamsContextType>(undefined);

export const SearchParamsProvider = ({
  value,
  children,
}: {
  value: SearchParamsContextType;
  children: ReactNode;
}) => {
  return (
    <SearchParamsContext.Provider value={value}>
      {children}
    </SearchParamsContext.Provider>
  );
};

export type SearchQueryContextType = {
  queries: {
    [key: string]: SearchQueryContextBaseType;
  };
};

export type SearchQueryContextBaseType = {
  paging: any;
  searchResults: any;
  data: any;
  queryState: any;
};

export const SearchQueryContext =
  createContext<SearchQueryContextType>(undefined);

export const SearchQueryProvider = ({
  value,
  children,
}: {
  value: SearchQueryContextBaseType;
  children: ReactNode;
}) => {
  return (
    <SearchQueryContext.Provider value={{ queries: { '': value } }}>
      {children}
    </SearchQueryContext.Provider>
  );
};

export const MultipleSearchQueryProvider = ({
  value,
  children,
}: {
  value: SearchQueryContextType;
  children: ReactNode;
}) => {
  return (
    <SearchQueryContext.Provider value={value}>
      {children}
    </SearchQueryContext.Provider>
  );
};

const SearchQueryWrapper = ({
  value,
  children,
}: {
  value: Parameters<UseSearchQueryFn>;
  children: ReactNode;
}) => {
  const searchQueryValue = useSearchQuery(...value);
  return (
    <SearchQueryProvider value={searchQueryValue}>
      {children}
    </SearchQueryProvider>
  );
};

const SearchProvider = ({
  value,
  children,
}: {
  value: ReturnType<UseSearchFn>;
  children: ReactNode;
}) => {
  return (
    <SearchParamsProvider value={value.params}>
      <SearchQueryWrapper value={value.query}>{children}</SearchQueryWrapper>
    </SearchParamsProvider>
  );
};

export default SearchProvider;
